import { authenticateEncounter, deleteEncounter } from '@backend/functions/gyn'
import { requestExamPdf, requestUltrasoundPdf } from '@backend/functions/other'
import { useDocument } from '@backend/index'
import { downloadFromStorage } from '@backend/storage/utils'
import { Box, Divider, VStack } from '@chakra-ui/react'
import { MyDotPhrasesModal } from '@components/Charting/DotPhraseTemplates/MyDotPhrasesModal'
import { useGynCareView } from '@components/Gyn/context'
import { useApp, useAuth, usePopUpMessage } from '@hb/react'
import {
  AnyObject,
  AuthenticateEncounterArgs,
  BaseAuthentication,
  Encounter,
  ENCOUNTERS,
  EncounterStatus,
  EncounterType,
  EncounterTypeConfig,
  objectToArray,
  SubmittedAuthentication,
  WithId,
} from '@hb/shared'
import {
  Dispatch,
  ForwardedRef,
  forwardRef,
  ReactElement,
  SetStateAction,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'
import { useField, useForm, useFormState } from 'react-final-form'
import { usePracticeProviders } from '../../../../components/Practices/hooks/usePracticeProviders'
import { useIsPracticeSuperAdmin } from '../../../../hooks/backend/auth/usePracticeRole'
import { CdiPromptBanner } from '../../EncounterView/CdiPromptBanner'
import { useEncounterErrors } from '../EncounterErrorsProvider'
import { useTimerFlush } from '../TimerFlushContext'
import { ElapsedTimeInput } from './ElapsedTimeInput'
import { EncounterFormHeaderActionRow } from './EncounterFormHeaderActionRow'
import { EncounterFormHeaderTitleRow } from './EncounterFormHeaderTitleRow'
import { HistoricalAuthModal } from './HistoricalAuthModal'
import { PracticeDotPhrasesModal } from './PracticeDotPhrasesModal'

export interface EncounterFormHeaderHandle {
  openAuthModal: () => void
}

interface EncounterFormHeaderProps<T extends EncounterType> {
  examId: string | null
  patientId: string
  assessmentId: string | null
  typeName: string
  examConfig: EncounterTypeConfig<T> | null
  examType: T
  onClose: () => void
  confirmingCancel: boolean
  setConfirmingCancel: Dispatch<SetStateAction<boolean>>
  allSectionsRendered: boolean
  // When provided, clicking Save Draft shows the Time & Work Log panel instead of submitting directly.
  onSaveDraft?: () => void
  // When provided, clicking Authenticate defers to the body (e.g. to show the Time & Work Log panel first).
  onRequestAuthenticate?: () => void
}

const EncounterFormHeaderInner = <T extends EncounterType>(
  {
    examId,
    patientId,
    assessmentId,
    typeName,
    examConfig,
    examType,
    onClose,
    confirmingCancel,
    setConfirmingCancel,
    allSectionsRendered,
    onSaveDraft,
    onRequestAuthenticate,
  }: EncounterFormHeaderProps<T>,
  ref: ForwardedRef<EncounterFormHeaderHandle>,
) => {
  const form = useForm()
  const { submit, reset } = form
  const { flushAllTimers } = useTimerFlush()
  const { dirty } = useFormState({
    subscription: { dirty: true },
  })
  const { practiceId } = useGynCareView()
  const { showError } = usePopUpMessage()
  const { tier1ErrorExists } = useEncounterErrors()
  const [myTemplatesOpen, setMyTemplatesOpen] = useState(false)
  const [practiceTemplatesOpen, setPracticeTemplatesOpen] = useState(false)
  const isPracticeSuperAdmin = useIsPracticeSuperAdmin(practiceId ?? '')
  const { appName } = useApp()

  // §A: Author identity display — shows saved noteAuthor or falls back to current user for new notes.
  const { authUser } = useAuth()

  // §A P3-A: Co-signature required by provider role flag.
  // Reads the current user's requiresCoSign flag from their practice-level user record.
  const { data: practiceProviders } = usePracticeProviders(practiceId)
  const userRequiresCoSign = useMemo(() => {
    if (!authUser?.uid || !practiceProviders) return false
    const myRecord = objectToArray(practiceProviders).find(p => p.id === authUser.uid)
    return myRecord?.requiresCoSign === true
  }, [authUser?.uid, practiceProviders])

  // §A P3-B: payer-level co-sign gate — primary insurance data not yet in form context;
  // add usePatientPrimaryPayer(patientId) hook when coverage context is wired here.
  const payerRequiresCoSign = false

  // Combined co-sign requirement — either provider role flag or payer flag triggers it.
  const coSignRequired = userRequiresCoSign || payerRequiresCoSign

  // Read the current co-sign selection so the authenticate button can be gated when required.
  const {
    input: { value: coSignedByValue },
  } = useField<string | undefined>('data.coSignedBy', { subscription: { value: true } })

  // §F: Auto-written by ElapsedTimeInput whenever the time log total changes.
  const {
    input: { value: totalMinutes },
  } = useField<number | undefined>('data.timeWorkLog.totalMinutes', {
    subscription: { value: true },
  })

  const { data: encounter } = useDocument<Encounter>(ENCOUNTERS, examId ?? '')

  const [submitting, setSubmitting] = useState(false)
  const [printing, setPrinting] = useState(false)
  const [authenticateModalOpen, setAuthenticateModalOpen] = useState(false)
  const [authChecked, setAuthChecked] = useState(false)

  useImperativeHandle(
    ref,
    () => ({
      openAuthModal: () => {
        setAuthChecked(false)
        setAuthenticateModalOpen(true)
      },
    }),
    [],
  )

  const [viewingHistoricalAuth, setViewingHistoricalAuth] = useState<
    BaseAuthentication<AnyObject> | 'current' | null
  >(null)
  const [pendingHistoricalAuth, setPendingHistoricalAuth] = useState<
    BaseAuthentication<AnyObject> | 'current' | null
  >(null)

  const hasAuthHistory = useMemo(
    () =>
      !!encounter?.authentication &&
      (!!encounter.draftData || (encounter.authentication.previousVersions?.length ?? 0) > 0),
    [encounter],
  )

  const handleViewHistoricalAuth = useCallback(
    (auth: BaseAuthentication<AnyObject> | 'current') => {
      if (dirty) {
        setPendingHistoricalAuth(auth)
      } else {
        setViewingHistoricalAuth(auth)
      }
    },
    [dirty],
  )

  const handleCancel = useCallback(() => {
    if (dirty && !confirmingCancel) {
      setConfirmingCancel(true)
    } else {
      onClose()
    }
  }, [dirty, onClose, confirmingCancel, setConfirmingCancel])

  const handleDiscard = useCallback(() => {
    reset()
  }, [reset])

  const handleSubmit = useCallback(async () => {
    flushAllTimers()
    if (onSaveDraft) {
      onSaveDraft()
      return
    }
    setSubmitting(true)
    await submit()
    setSubmitting(false)
  }, [flushAllTimers, onSaveDraft, submit])

  const handlePrint = useCallback(async () => {
    if (!examId || !practiceId) return
    if (dirty) {
      showError('You have unsaved changes. Please save or discard them before printing.')
      return
    }
    setPrinting(true)
    try {
      const storagePath =
        examType === EncounterType.ULTRASOUND
          ? (await requestUltrasoundPdf({ practiceId, patientId, examId })).data.storagePath
          : (await requestExamPdf({ practiceId, patientId, examIds: [examId] })).data.storagePath
      await downloadFromStorage(storagePath)
    } catch (err) {
      console.error('Failed to generate PDF', err)
    } finally {
      setPrinting(false)
    }
  }, [examId, patientId, practiceId, dirty, showError, examType])

  const handleAuthenticate = useCallback(
    async (auth: SubmittedAuthentication): Promise<void> => {
      if (!practiceId) throw new Error('Practice ID is missing')
      flushAllTimers()
      const { values } = form.getState()
      await authenticateEncounter({
        appName,
        examId,
        type: examType,
        patientId,
        practiceId,
        assessmentId,
        config: examConfig,
        authentication: auth,
        ...values,
      } as unknown as AuthenticateEncounterArgs)
      onClose()
    },
    [
      flushAllTimers,
      form,
      examId,
      examType,
      patientId,
      practiceId,
      assessmentId,
      examConfig,
      appName,
      onClose,
    ],
  )

  // Draft deletion — only the rendering or supervising provider assigned to the draft may delete
  // it, and only while it is still a draft. Authenticated encounters are part of the permanent
  // record and cannot be deleted.
  const canDeleteDraft = useMemo(() => {
    if (!examId || !encounter) return false
    if (encounter.status === EncounterStatus.AUTHENTICATED) return false
    return (
      encounter.renderingProvider === authUser?.uid ||
      encounter.supervisingProvider === authUser?.uid
    )
  }, [examId, encounter, authUser?.uid])

  const handleDeleteDraft = useCallback(async () => {
    if (!examId) return
    await deleteEncounter({ appName, encounterId: examId })
    onClose()
  }, [examId, appName, onClose])

  const cannotPrintMessage = useMemo(() => {
    if (dirty) {
      return 'You have unsaved changes. Please save or discard them before printing.'
    }
    return null
  }, [dirty])

  const cannotAuthenticateMessage = useMemo(() => {
    if (!allSectionsRendered) return 'Complete all steps before authenticating'
    if (tier1ErrorExists) return 'Required fields missing — resolve errors before authenticating'
    // §E: Block authentication if any string field still contains unresolved [placeholder] tokens.
    // Only tests string values — avoids false positives from JSON array syntax in non-string fields.
    const hasPlaceholder = (obj: unknown): boolean => {
      if (typeof obj === 'string') return /\[[^\]]+\]/.test(obj)
      if (Array.isArray(obj)) return obj.some(hasPlaceholder)
      if (obj !== null && typeof obj === 'object') return Object.values(obj).some(hasPlaceholder)
      return false
    }
    if (hasPlaceholder(form.getState().values ?? {})) {
      return 'Unresolved template placeholders found — replace all [bracketed] text before authenticating'
    }
    // §A P3-A: Co-signature required — block authentication until co-signer selected.
    if (coSignRequired && !coSignedByValue)
      return 'A co-signing provider is required before this note can be authenticated'
    // §F: Total visit time cannot be zero — §2.10 applies to antepartum and postpartum only.
    const isObEncounter =
      examType === EncounterType.ANTEPARTUM || examType === EncounterType.POSTPARTUM
    if (isObEncounter && (totalMinutes ?? 0) === 0)
      return 'Total visit time cannot be zero — enter at least one time field'
    return null
  }, [
    allSectionsRendered,
    tier1ErrorExists,
    form,
    coSignRequired,
    coSignedByValue,
    totalMinutes,
    examType,
  ])

  return (
    <VStack
      w="100%"
      // borderColor="gray.300"
      bg="white"
      // borderBottomWidth="1px"
      spacing={0}
      divider={<Divider borderColor="gray.300" />}>
      <EncounterFormHeaderTitleRow
        typeName={typeName}
        examId={examId}
        examType={examType}
        examConfig={examConfig}
        practiceId={practiceId}
        isPracticeSuperAdmin={isPracticeSuperAdmin}
        onOpenMyTemplates={() => setMyTemplatesOpen(true)}
        onOpenPracticeTemplates={() => setPracticeTemplatesOpen(true)}
        cannotPrintMessage={cannotPrintMessage}
        printing={printing}
        onPrint={handlePrint}
        hasAuthHistory={hasAuthHistory}
        encounter={encounter}
        onViewHistoricalAuth={handleViewHistoricalAuth}
      />
      <EncounterFormHeaderActionRow
        typeName={typeName}
        examId={examId}
        examType={examType}
        encounter={encounter}
        practiceId={practiceId}
        confirmingCancel={confirmingCancel}
        setConfirmingCancel={setConfirmingCancel}
        pendingHistoricalAuth={pendingHistoricalAuth}
        setPendingHistoricalAuth={setPendingHistoricalAuth}
        setViewingHistoricalAuth={setViewingHistoricalAuth}
        authenticateModalOpen={authenticateModalOpen}
        setAuthenticateModalOpen={setAuthenticateModalOpen}
        authChecked={authChecked}
        setAuthChecked={setAuthChecked}
        coSignRequired={coSignRequired}
        cannotAuthenticateMessage={cannotAuthenticateMessage}
        submitting={submitting}
        canDeleteDraft={canDeleteDraft}
        onCancel={handleCancel}
        onDiscard={handleDiscard}
        onSubmit={handleSubmit}
        onAuthenticate={handleAuthenticate}
        onDeleteDraft={handleDeleteDraft}
        onRequestAuthenticate={onRequestAuthenticate}
      />
      <ElapsedTimeInput />
      {/* §6.7 "Match action": provider CDI prompt during charting — providers-app only.
          Detections only exist post-save; the header holds a live subscription to the
          saved encounter, so newly-scanned items stream in with no extra fetch. Capped
          height so multiple simultaneous detections don't over-grow the sticky header. */}
      {appName !== 'app' && examId && encounter ? (
        <Box w="100%" maxH="180px" overflowY="auto">
          <CdiPromptBanner
            encounter={{ ...encounter, id: examId } as WithId<Encounter>}
            encounterId={examId}
          />
        </Box>
      ) : null}
      {/* §E: Personal dot phrase template management modal */}
      {myTemplatesOpen && authUser?.uid ? (
        <MyDotPhrasesModal isOpen onClose={() => setMyTemplatesOpen(false)} userId={authUser.uid} />
      ) : null}
      {/* §E: Practice dot phrase template management modal — practice super-admins only */}
      {practiceTemplatesOpen && isPracticeSuperAdmin && practiceId ? (
        <PracticeDotPhrasesModal
          isOpen
          onClose={() => setPracticeTemplatesOpen(false)}
          practiceId={practiceId}
        />
      ) : null}
      {viewingHistoricalAuth && encounter && examId ? (
        <HistoricalAuthModal
          viewingHistoricalAuth={viewingHistoricalAuth}
          encounter={encounter}
          examId={examId}
          patientId={patientId}
          onClose={() => setViewingHistoricalAuth(null)}
        />
      ) : null}
    </VStack>
  )
}

export const EncounterFormHeader = forwardRef(EncounterFormHeaderInner) as <
  T extends EncounterType,
>(
  props: EncounterFormHeaderProps<T> & { ref?: React.Ref<EncounterFormHeaderHandle> },
) => ReactElement
